import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, ChevronDown, Search } from 'lucide-react';
import { LSRoutes } from '../../LYSEPHelper';
import { getCategorizedModules, CATEGORY_LABELS, MODULE_CATEGORIES, CATEGORY_ICONS } from '../modulesConfig';

/**
 * Sidebar Menu Component
 * Renders navigation menu items grouped by categories
 */
export const SidebarMenu = ({ currentPage, canAccessModule }) => {
    // Get categorized modules
    const categorizedModules = getCategorizedModules(canAccessModule);
    const [searchTerm, setSearchTerm] = useState('');

    // State for collapsed categories
    const categoryOrder = [
        MODULE_CATEGORIES.MAIN,
        MODULE_CATEGORIES.TOOLS,
        MODULE_CATEGORIES.LYSEP_WEBSITE,
        MODULE_CATEGORIES.USER_MANAGEMENT,
        MODULE_CATEGORIES.SERVER,
        MODULE_CATEGORIES.SETTINGS
    ];

    const [openCategories, setOpenCategories] = useState(
        categoryOrder.reduce((acc, key) => ({ ...acc, [key]: true }), {})
    );

    // State for collapsed sub-menus
    const [openSubMenus, setOpenSubMenus] = useState({});

    const toggleCategory = (categoryKey) => {
        setOpenCategories(prev => ({
            ...prev,
            [categoryKey]: !prev[categoryKey]
        }));
    };

    const handleSubMenuClick = (e, moduleName) => {
        e.preventDefault();
        setOpenSubMenus(prev => ({
            ...prev,
            [moduleName]: !prev[moduleName]
        }));
    };

    // Helper to check if a module or its children are active
    const isModuleActive = (module) => {
        if (currentPage === module.path) return true;
        if (module.children) {
            return module.children.some(child => currentPage === child.path);
        }
        return false;
    };

    // Filter modules based on search
    const filterModules = (modules) => {
        if (!searchTerm) return modules;
        return modules.reduce((acc, module) => {
            const matchesTitle = module.title.toLowerCase().includes(searchTerm.toLowerCase());
            const filteredChildren = module.children ? filterModules(module.children) : [];

            if (matchesTitle || filteredChildren.length > 0) {
                acc.push({
                    ...module,
                    children: filteredChildren
                });
            }
            return acc;
        }, []);
    };

    /**
     * Render a single module item (recursive)
     */
    const renderModuleItem = (module, index, level = 0) => {
        // Check access
        if (module.moduleName !== "profile" && !canAccessModule(module.moduleName)) {
            return null;
        }

        const hasChildren = module.children && module.children.length > 0;
        const isSubOpen = searchTerm ? true : !!openSubMenus[module.moduleName];
        const isActive = isModuleActive(module);

        // Modern Styling Classes
        const baseClasses = `
            group flex items-center justify-between px-3 py-2.5 mx-2 rounded-xl 
            text-sm font-medium transition-all duration-200 cursor-pointer mb-1 relative overflow-hidden
        `;
        const indentClass = level > 0 ? "pl-11" : "";

        let activeClasses = "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04]";
        let activeIndicator = null;

        if (isActive) {
            if (hasChildren) {
                activeClasses = "text-white bg-white/[0.08]";
            } else {
                activeClasses = "bg-[#fd7000]/10 text-[#fd7000]";
                activeIndicator = (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-[#fd7000] rounded-r-full shadow-[0_0_12px_rgba(253,112,0,0.6)]" />
                );
            }
        }

        const combinedClasses = `${baseClasses} ${activeClasses} ${indentClass}`;

        if (hasChildren) {
            return (
                <React.Fragment key={`${level}-${index}`}>
                    <div
                        onClick={(e) => handleSubMenuClick(e, module.moduleName)}
                        className={combinedClasses}
                    >
                        {isActive && !searchTerm && activeIndicator}
                        <div className="flex items-center gap-3 z-10">
                            <span className={`transition-colors duration-200 ${isActive ? 'text-[#fd7000]' : 'text-gray-500 group-hover:text-gray-300'}`}>
                                {module.icon}
                            </span>
                            <span>{module.title}</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 opacity-50 transition-transform duration-300 ${isSubOpen ? 'rotate-180' : ''}`} />
                    </div>

                    {/* Submenu Content */}
                    <div className={`grid transition-all duration-200 ease-in-out ${isSubOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                        <div className="overflow-hidden">
                            <div className="mt-1 space-y-0.5 relative">
                                <div className="absolute left-[26px] top-0 bottom-0 w-px bg-white/[0.05]" />
                                {module.children.map((child, childIndex) =>
                                    renderModuleItem(child, childIndex, level + 1)
                                )}
                            </div>
                        </div>
                    </div>
                </React.Fragment>
            );
        }

        return (
            <Link key={`${level}-${index}`} to={LSRoutes.Dashboard(module.path)} className="block">
                <div className={combinedClasses}>
                    {isActive && activeIndicator}
                    <div className="flex items-center gap-3 z-10">
                        <span className={`transition-colors duration-200 ${isActive ? 'text-[#fd7000]' : 'text-gray-500 group-hover:text-gray-300'}`}>
                            {module.icon}
                        </span>
                        <span>{module.title}</span>
                    </div>
                </div>
            </Link>
        );
    };

    /**
     * Render a category section
     */
    const renderCategory = (categoryKey) => {
        let categoryModules = categorizedModules[categoryKey];

        if (searchTerm) {
            categoryModules = filterModules(categoryModules || []);
        }

        if (!categoryModules || categoryModules.length === 0) {
            return null;
        }

        const accessibleModules = categoryModules.filter(module => {
            if (module.moduleName === "profile") return true;
            return canAccessModule(module.moduleName);
        });

        if (accessibleModules.length === 0) {
            return null;
        }

        const isCategoryOpen = searchTerm ? true : !!openCategories[categoryKey];
        const CategoryIcon = CATEGORY_ICONS[categoryKey];

        return (
            <div key={categoryKey} className={`
                mx-2 mb-4 rounded-2xl transition-all duration-300
                ${isCategoryOpen && !searchTerm ? 'bg-white/[0.02] border border-white/[0.05] pb-2' : 'bg-transparent border border-transparent'}
            `}>
                {!searchTerm && (
                    <button
                        onClick={() => toggleCategory(categoryKey)}
                        className={`
                            w-full px-4 py-3 flex items-center justify-between group/cat transition-all duration-300
                            ${isCategoryOpen ? 'text-white' : 'text-gray-400 hover:text-gray-200'}
                        `}
                    >
                        <div className="flex items-center gap-2.5 flex-1">
                            <div className={`
                                p-1.5 rounded-lg transition-colors duration-300
                                ${isCategoryOpen ? 'bg-[#fd7000]/10 text-[#fd7000]' : 'bg-white/[0.03] text-gray-500 group-hover/cat:text-gray-400'}
                            `}>
                                {React.cloneElement(CategoryIcon, { className: 'w-3.5 h-3.5' })}
                            </div>
                            <span className="text-[11px] font-bold uppercase tracking-widest transition-colors">
                                {CATEGORY_LABELS[categoryKey]}
                            </span>
                        </div>
                        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform duration-300 ${isCategoryOpen ? 'rotate-180' : ''}`} />
                    </button>
                )}

                <div className={`grid transition-all duration-300 ease-in-out ${isCategoryOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                    <div className="overflow-hidden">
                        <div className={`${!searchTerm ? 'pt-1' : ''}`}>
                            {accessibleModules.map((module, index) => renderModuleItem(module, index))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col pb-4 h-full">
            {/* Search Bar */}
            <div className="px-4 mb-2 pt-2 sticky top-0 bg-[#0f1115]/95 z-20 pb-2 backdrop-blur-xl">
                <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-gray-500 group-focus-within:text-[#fd7000] transition-colors" />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-white/[0.08] rounded-xl leading-5 bg-white/[0.03] text-gray-300 placeholder-gray-500 focus:outline-none focus:bg-white/[0.06] focus:border-[#fd7000]/50 focus:ring-1 focus:ring-[#fd7000]/50 sm:text-sm transition-all shadow-inner"
                        placeholder="Modül ara..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Modules List */}
            <div className="flex-1 overflow-y-auto custom-scrollbar px-1">
                {categoryOrder.map(renderCategory)}

                {searchTerm && categoryOrder.every(key => filterModules(categorizedModules[key] || []).length === 0) && (
                    <div className="text-center py-8 text-gray-500 text-sm">
                        Sonuç bulunamadı.
                    </div>
                )}

                <div className="my-4 border-t border-white/[0.06] mx-4"></div>

                <Link to={LSRoutes.Dashboard("logout")}>
                    <div className="group flex items-center gap-3 px-3 py-2.5 mx-2 rounded-xl text-sm font-medium text-gray-400 hover:bg-red-500/10 hover:text-red-500 transition-all cursor-pointer">
                        <LogOut className="w-5 h-5 group-hover:scale-110 transition-transform" />
                        <span>Çıkış Yap</span>
                    </div>
                </Link>
            </div>
        </div>
    );
};
